/**
 * bb-cart-wishlist.js — Bara Bros
 * "Save for Later" bridge: cart line item → Add to Board popup → remove from cart.
 *
 * Flow:
 *  1. Customer clicks .bb-sfl-btn on a cart line item.
 *  2. WishlistEngine.openPopup() is called with item data from data-sfl-* attrs.
 *  3. Customer picks a board → wishlist-engine.js dispatches bb:wl:popup-saved.
 *  4. If the saved productId matches the pending product → dispatch onLineItemRemove
 *     on the cart component (same mechanism as the native remove button).
 *  5. If popup is dismissed (bb:wl:popup-closed) without a save → pending cleared,
 *     cart item stays untouched.
 *  6. If a different product is saved (e.g. heart button mid-flow) → IDs won't
 *     match → pending cleared, cart item stays untouched.
 *
 * Guest users: WishlistEngine saves to localStorage only; no sync fires.
 * Logged-in users: existing bb:wl:changed → bb-wishlist-sync.js handles sync.
 */
(function (global) {
  'use strict';

  // Guard: only initialise once even if snippet renders in both main cart + drawer
  if (global._bbSflLoaded) return;
  global._bbSflLoaded = true;

  var _debug = (global.BB_WISHLIST_DEBUG === true);

  // ── Inject button styles ──────────────────────────────────────────────────────
  if (!document.getElementById('bb-sfl-style')) {
    var styleEl = document.createElement('style');
    styleEl.id  = 'bb-sfl-style';
    styleEl.textContent =
      // Baseline button (main cart fallback — drawer overrides via section CSS)
      '.bb-sfl-btn{' +
        'display:inline-flex;align-items:center;gap:5px;' +
        'margin-top:var(--bb-sfl-mt,10px);' +
        'background:none;border:none;padding:2px 0;' +
        'cursor:pointer;font-family:inherit;' +
        'font-size:var(--bb-sfl-fs,10px);font-weight:700;letter-spacing:.6px;' +
        'color:rgba(0,231,255,0.5);text-transform:uppercase;' +
        'transition:color .15s,filter .15s;line-height:1.4;' +
      '}' +
      '.bb-sfl-btn:hover,.bb-sfl-btn:focus-visible{color:#00E7FF;outline:none;}' +
      '.bb-sfl-btn:active{opacity:.75;}' +
      // Icon span
      '.bb-sfl-icon{display:inline-flex;align-items:center;line-height:1;transition:color .15s,transform .15s;}' +
      '.bb-sfl-icon:empty::before{' +
        'content:"\\2665";' +
        'font-size:var(--bb-sfl-is,12px);font-weight:400;letter-spacing:0;' +
        'color:rgba(0,231,255,0.35);display:inline-block;line-height:1;' +
        'transition:color .15s,transform .15s;' +
      '}' +
      '.bb-sfl-icon svg{width:var(--bb-sfl-is,12px);height:var(--bb-sfl-is,12px);fill:currentColor;display:block;overflow:visible;}' +
      '.bb-sfl-btn:hover .bb-sfl-icon:empty::before{color:#FF00B8;transform:scale(1.2);}' +
      '.bb-sfl-btn:hover .bb-sfl-icon svg{fill:#FF00B8;transform:scale(1.2);}' +
      // Price cents
      '.bb-price-cents{display:inline-block;line-height:1;vertical-align:baseline;}';
    document.head.appendChild(styleEl);
  }

  // ── Pending save state ────────────────────────────────────────────────────────
  var _pendingLine      = null; // cart line index (1-based) to remove after save
  var _pendingProductId = null; // must match the productId in bb:wl:popup-saved

  function clearPending() {
    _pendingLine      = null;
    _pendingProductId = null;
  }

  // ── Button click — event delegation, survives cart section re-renders ─────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.bb-sfl-btn');
    if (!btn) return;

    var WE = global.WishlistEngine;
    if (!WE || typeof WE.openPopup !== 'function') return;

    e.preventDefault();
    e.stopPropagation();

    _pendingLine      = btn.dataset.sflLine;
    _pendingProductId = String(btn.dataset.sflProductId || '');

    if (_debug) console.log('[BB SFL] Opening board picker — product:', _pendingProductId, '| cart line:', _pendingLine);

    WE.openPopup({
      productId: _pendingProductId,
      title:     btn.dataset.sflTitle  || '',
      image:     btn.dataset.sflImage  || '',
      url:       btn.dataset.sflUrl    || '',
      price:     btn.dataset.sflPrice  || '',
      vendor:    btn.dataset.sflVendor || ''
    });
  });

  // ── Popup saved → remove matching cart line ───────────────────────────────────
  window.addEventListener('bb:wl:popup-saved', function (e) {
    if (!_pendingLine || !_pendingProductId) return;

    var savedId = String((e.detail && e.detail.productId) || '');

    if (savedId !== _pendingProductId) {
      // A different product was saved (e.g. via a heart button) — don't touch cart
      if (_debug) console.log('[BB SFL] Saved product', savedId, '≠ pending', _pendingProductId, '— cart unchanged');
      clearPending();
      return;
    }

    var line = parseInt(_pendingLine, 10);
    if (_debug) console.log('[BB SFL] Save confirmed — removing cart line', line);
    clearPending();

    // Use the same custom event the native remove button dispatches
    var container = document.querySelector('cart-items-component') ||
                    document.querySelector('cart-drawer-items');
    if (!container) return;

    container.dispatchEvent(new CustomEvent('onLineItemRemove', {
      detail:  { line: line },
      bubbles: true
    }));
  });

  // ── Popup closed without saving → clear pending so stale state doesn't linger ─
  window.addEventListener('bb:wl:popup-closed', function () {
    if (_pendingLine) {
      if (_debug) console.log('[BB SFL] Popup closed without save — cart line', _pendingLine, 'stays');
      clearPending();
    }
  });

  // ── Price split: large dollar / superscript cents ─────────────────────────────
  var _splitObserver = null;

  function splitPrices() {
    var cfg = global.BBCartConfig || {};
    if (!cfg.splitPrice) return;
    var em    = ((cfg.centsSize  || 60) / 100).toFixed(2);
    var raise = (cfg.centsRaise !== undefined ? cfg.centsRaise : 4);
    // Target both cart drawer and main cart page
    var sel = '#Cart-Drawer .price:not(.bb-price-split), cart-items-component .price:not(.bb-price-split)';
    document.querySelectorAll(sel).forEach(function (el) {
      if (el.children.length > 0) return;
      var text = el.textContent.trim();
      var m = text.match(/^(.+?)([.,])(\d{2})$/);
      if (!m) return;
      el.classList.add('bb-price-split');
      el.innerHTML =
        '<span class="bb-price-whole">' + m[1] + '</span>' +
        '<span class="bb-price-cents" style="font-size:' + em + 'em;vertical-align:' + raise + 'px;">' + m[2] + m[3] + '</span>';
    });
  }

  function initSplitPrices() {
    var cfg = global.BBCartConfig || {};
    if (!cfg.splitPrice) return;
    splitPrices();
    if (_splitObserver) return;
    _splitObserver = new MutationObserver(splitPrices);
    // Observe both drawer items and main cart items
    var targets = [
      document.getElementById('CartDrawer-CartItems'),
      document.getElementById('main-cart-items')
    ];
    targets.forEach(function (t) {
      if (t) _splitObserver.observe(t, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSplitPrices);
  } else {
    initSplitPrices();
  }

})(window);
